import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  AuthService,
  RoleType,
} from '@anihis/core/data-access';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { TranslocoService, translate } from '@jsverse/transloco';
import { getEmailRequestLanguage } from '@anihis/shared/src/functions/email-request-language';
import { catchError, finalize, map, of, switchMap, tap } from 'rxjs';
import { LoadingStatus } from '../../shared/enums/loading';
import { extractServerErrorMessage } from '../../shared/functions/extract-server-error';

type ActivateAccountState = {
  status: LoadingStatus;
  error: string | null;
  role: RoleType;
};

const initialState: ActivateAccountState = {
  status: LoadingStatus.Loaded,
  error: null,
  role: RoleType.ClinicOwner,
};

export const ActivateAccountStore = signalStore(
  withState(initialState),
  withMethods(
    (
      store,
      authService = inject(AuthService),
      router = inject(Router),
      snackBar = inject(MatSnackBar),
      transloco = inject(TranslocoService),
    ) => ({
      setRole(role: RoleType) {
        patchState(store, { role });
      },

      requestActivation(email: string) {
        patchState(store, { status: LoadingStatus.Loading, error: null });
        const role = store.role();

        return authService
          .authIdentifyPost({ email, role })
          .pipe(
            switchMap((response) => {
              if (response.action === 'ClaimAccount') {
                return authService
                  .authRequestClaimAccountPost({
                    email,
                    role,
                    language: getEmailRequestLanguage(transloco.getActiveLang()),
                  })
                  .pipe(map(() => 'claim' as const));
              }

              if (role === RoleType.Manager) {
                return of('manager-missing' as const);
              }

              return authService
                .authRequestRestoreClinicPost({
                  email,
                  language: getEmailRequestLanguage(transloco.getActiveLang()),
                })
                .pipe(map(() => 'clinic' as const));
            }),
            tap((type) => {
              const message =
                type === 'claim'
                  ? translate('Activation link has been sent to your email!')
                  : type === 'manager-missing'
                    ? translate('No manager account found for this email. Contact Anihis support.')
                    : translate(
                        'Clinic restoration link has been sent to your email!',
                      );

              snackBar.open(message, translate('Close'), {
                duration: 5000,
                horizontalPosition: 'center',
                verticalPosition: 'top',
                panelClass: [
                  type === 'manager-missing' ? 'error-snackbar' : 'success-snackbar',
                ],
              });

              if (type !== 'manager-missing') {
                router.navigateByUrl('/login');
              }
            }),
            finalize(() => patchState(store, { status: LoadingStatus.Loaded })),
            catchError((err) => {
              const errorMsg = extractServerErrorMessage(
                err,
                'Failed to send activation email. Please try again.',
              );

              patchState(store, { error: errorMsg });

              snackBar.open(errorMsg, translate('Close'), {
                duration: 7000,
                horizontalPosition: 'center',
                verticalPosition: 'top',
                panelClass: ['error-snackbar'],
              });

              return of(null);
            }),
          );
      },

      resetError() {
        patchState(store, { error: null });
      },
    }),
  ),
);
